import { CopyableTextField, DeactivateLinkButton, ExpirationDatePicker, PdfSection } from './ShareBlueprintModal.utils';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import {
  useDownloadBlueprintPdf,
  useGetSharedBlueprintToken,
  useShareBlueprint,
  useUpdateSharedBlueprintExpiration,
} from 'features/blueprints';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { DialogModal } from 'greener-ui-library';
import { currencyCodeToLocale } from 'utils/localization';
import { Blueprint } from 'types';

interface ShareBlueprintModalProps {
  open: boolean;
  onClose: () => void;
  blueprintName: string;
  blueprintId: number;
  chartScreenshot?: Blob | null;
  blueprintDetails?: Blueprint;
}

interface GeneratedLink {
  webUrl: string;
  accessCode: string;
  expirationDate: Date;
  createdAt: string;
  shareToken: string;
}

export const ShareBlueprintModal = ({
  open,
  onClose,
  blueprintName,
  blueprintId,
  chartScreenshot,
  blueprintDetails,
}: ShareBlueprintModalProps) => {
  const { t } = useTranslation(['blueprints']);
  const { enqueueSnackbar } = useSnackbar();
  const { copyToClipboard, copiedField } = useCopyToClipboard(1500);
  const [generatedLink, setGeneratedLink] = useState<GeneratedLink | null>(null);
  const { sharedToken, isLoading: isLoadingToken } = useGetSharedBlueprintToken({ blueprintId, enabled: open });
  const { shareBlueprint, isLoading: isSharingBlueprint } = useShareBlueprint();
  const { updateExpiration } = useUpdateSharedBlueprintExpiration();
  const currencyCode = blueprintDetails?.calculations?.[0]?.currencyCode || 'eur';
  const locale = currencyCodeToLocale(currencyCode);
  const { downloadBlueprintPdf, isLoading: isDownloadingPdf } = useDownloadBlueprintPdf(locale);
  const pdfFileName = `${blueprintName.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('en-GB').replace(/\//g, '')}.pdf`;

  // Set generated link from existing token when it's loaded
  useEffect(() => {
    if (sharedToken && sharedToken.tsExpiration) {
      const expirationDate = new Date(sharedToken.tsExpiration);

      // Validate the date before setting state
      if (isNaN(expirationDate.getTime())) {
        console.error('Invalid expiration date from API:', sharedToken.tsExpiration);
        setGeneratedLink(null);
        return;
      }

      // Construct the share URL using current origin and share token
      const shareUrl = `${window.location.origin}/shared/${sharedToken.shareToken}`;

      setGeneratedLink({
        webUrl: shareUrl,
        accessCode: sharedToken.accessCode,
        expirationDate,
        shareToken: sharedToken.shareToken,
        createdAt: new Date().toLocaleString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
      });
    } else if (!isLoadingToken) {
      // Reset if no token exists and we're not loading
      setGeneratedLink(null);
    }
  }, [sharedToken, isLoadingToken]);

  const handleGenerateLink = async () => {
    const expirationDate = new Date();
    expirationDate.setMonth(expirationDate.getMonth() + 1);

    try {
      const result = await shareBlueprint({
        blueprintId,
        tsExpiration: expirationDate.toISOString(),
      });

      if (result?.data) {
        const now = new Date();
        const data = result.data;
        // Construct the share URL using current origin and share token
        const shareUrl = `${window.location.origin}/shared/${data.shareToken}`;

        setGeneratedLink({
          webUrl: shareUrl,
          accessCode: data.accessCode,
          expirationDate: new Date(data.tsExpiration),
          shareToken: data.shareToken,
          createdAt: `${now.toLocaleDateString('en-GB')}  ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
        });
      }
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.generateFailed'), { variant: 'error' });
    }
  };

  const handleExpirationDateChange = async (date: Date | null) => {
    if (!date || !generatedLink || !generatedLink.shareToken) {
      return;
    }

    try {
      await updateExpiration({
        blueprintId,
        shareToken: generatedLink.shareToken,
        tsExpiration: date.toISOString(),
      });

      setGeneratedLink({
        ...generatedLink,
        expirationDate: date,
      });

      enqueueSnackbar(t('blueprints:share.expirationDateUpdated'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.updateFailed'), { variant: 'error' });
    }
  };

  const handleDeactivateLink = () => {
    setGeneratedLink(null);
  };

  const handleDownloadPdf = async () => {
    try {
      await downloadBlueprintPdf({
        blueprintId,
        fileName: pdfFileName,
        screenshot: chartScreenshot,
      });

      enqueueSnackbar(t('blueprints:share.pdfDownloadSuccess'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.pdfDownloadFailed'), { variant: 'error' });
    }
  };

  return (
    <DialogModal
      isOpen={open}
      closeModal={onClose}
      title={t('blueprints:share.title')}
      confirmButtonText={t('blueprints:share.done')}
      hideCancelButton
      paperProps={{ sx: { overflow: 'visible' } }}
    >
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} mb={3}>
          <Typography variant="subtitle2">{t('blueprints:share.webVersion')}</Typography>

          {isLoadingToken ? (
            // Loading state
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : !generatedLink ? (
            // Before link generation
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <TextField
                data-testid="generate-link-textfield"
                value=""
                placeholder={t('blueprints:share.generateLinkPlaceholder')}
                label={t('blueprints:share.shareableLinkToWeb')}
                variant="outlined"
                disabled
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontSize: '16px',
                    color: (theme) => theme.palette.text.secondary,
                    '& input': {
                      padding: '8px 12px',
                    },
                    '& input::placeholder': {
                      color: (theme) => theme.palette.grey[600],
                      opacity: 1,
                    },
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: '12px',
                    color: (theme) => theme.palette.text.secondary,
                    transform: 'translate(14px, -9px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(1)',
                    },
                    '&.Mui-disabled': {
                      color: (theme) => theme.palette.grey[800],
                    },
                  },
                }}
                sx={{
                  maxWidth: { xs: '100%', sm: '400px' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.grey[400],
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateLink}
                disabled={isSharingBlueprint}
                sx={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {isSharingBlueprint ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('blueprints:share.generateLink')
                )}
              </Button>
            </Box>
          ) : (
            // After link generation
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Web URL and Access Code Row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <CopyableTextField
                  value={generatedLink.webUrl}
                  label={t('blueprints:share.shareableLinkToWeb')}
                  onCopy={() => copyToClipboard(generatedLink.webUrl, 'webUrl')}
                  isCopied={copiedField === 'webUrl'}
                />
                <CopyableTextField
                  value={generatedLink.accessCode}
                  label={t('blueprints:share.accessCodeLabel')}
                  onCopy={() => copyToClipboard(generatedLink.accessCode, 'accessCode')}
                  isCopied={copiedField === 'accessCode'}
                  flex={{ xs: '1 1 100%' }}
                />
              </Box>

              {/* Expiration Date and Deactivate Button */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                {generatedLink.expirationDate && !isNaN(generatedLink.expirationDate.getTime()) && (
                  <ExpirationDatePicker
                    selectedDate={generatedLink.expirationDate}
                    onChange={handleExpirationDateChange}
                    label={t('blueprints:share.linkExpirationDate')}
                    disabled={isSharingBlueprint}
                  />
                )}

                <Box sx={{ alignSelf: 'flex-start' }}>
                  <DeactivateLinkButton
                    onDeactivate={handleDeactivateLink}
                    shareToken={generatedLink.shareToken}
                    blueprintId={blueprintId}
                  />
                </Box>

                <Typography variant="body1" color="primary">
                  {t('blueprints:share.linkCreated')} {generatedLink.createdAt}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <PdfSection pdfFileName={pdfFileName} onDownloadPdf={handleDownloadPdf} isDownloading={isDownloadingPdf} />
      </>
    </DialogModal>
  );
};
